import { DocumentType } from '@documents-upload/types';
import { NEW_EXPIRATION_DATE_INITIAL_DATE } from '../../../../../constants';
import { addYears, getDate, getMonth, getYear, isAfter, isBefore, isEqual, parse, setYear, subDays } from 'date-fns';

const ID_CARD_EXPIRY_SUPERIOR_LIMIT = 11;
const MANDATORY_FIELD_MESSAGE = 'Questo campo è obbligatorio.';
const INVALID_DATE_MESSAGE = 'La data non è valida. Verificala sul tuo documento.';
const DATE_FORMAT = 'yyyy-MM-dd';

export const handleDrivingLicenceValidationExpirationDate = (
  expiryDate: string,
  issueDate: string,
  birthDate: string
) => {
  if (!expiryDate) return MANDATORY_FIELD_MESSAGE;

  const parsedExpiryDate = parse(expiryDate, DATE_FORMAT, new Date());
  const parsedIssueDate = parse(issueDate, DATE_FORMAT, new Date());
  const parsedBirthDate = parse(birthDate, DATE_FORMAT, new Date());

  parsedExpiryDate.setHours(0, 0, 0, 0);
  parsedIssueDate.setHours(0, 0, 0, 0);

  if (!isAfter(parsedExpiryDate, parsedIssueDate)) {
    return INVALID_DATE_MESSAGE;
  }

  const sameDayAndMonth =
    getDate(parsedExpiryDate) === getDate(parsedBirthDate) && getMonth(parsedExpiryDate) === getMonth(parsedBirthDate);

  if (!sameDayAndMonth) {
    return INVALID_DATE_MESSAGE;
  }

  const yearsDifference = getYear(parsedExpiryDate) - getYear(parsedIssueDate);

  if (yearsDifference > 11) {
    return INVALID_DATE_MESSAGE;
  }
};

export const handleIdentityCardValidationExpirationDate = (
  expiryDate: string,
  issueDate: string,
  birthDate: string
) => {
  const parsedExpiryDate = parse(expiryDate, DATE_FORMAT, new Date());
  const parsedIssueDate = parse(issueDate, DATE_FORMAT, new Date());
  const expectedExpiringDate = getIdCardExpirationDateByBirthDate(birthDate, issueDate, DocumentType.ID_CARD);
  const feb29InferiorLimitDate = new Date(getYear(parsedIssueDate) + 9, 1, 28);

  if (!expiryDate) return MANDATORY_FIELD_MESSAGE;

  if (!isBirthDateFebruaryThe29th(birthDate)) {
    if (
      isBefore(parsedExpiryDate, expectedExpiringDate) ||
      isAfter(parsedExpiryDate.setHours(0, 0, 0, 0), expectedExpiringDate) ||
      getYear(parsedExpiryDate) > getYear(parsedIssueDate) + ID_CARD_EXPIRY_SUPERIOR_LIMIT ||
      !areIssueDateMonthAndDayEqualToBirthMonthAndDay(parsedExpiryDate, birthDate)
    ) {
      return INVALID_DATE_MESSAGE;
    }
  } else {
    if (isBefore(parsedExpiryDate, feb29InferiorLimitDate)) return INVALID_DATE_MESSAGE;
  }
};

export const handlePassportValidationExpirationDate = (expiryDate: string, issueDate: string) => {
  if (!expiryDate) {
    return MANDATORY_FIELD_MESSAGE;
  }

  const parsedExpiryDate = parse(expiryDate, DATE_FORMAT, new Date());
  const parsedIssueDate = parse(issueDate, DATE_FORMAT, new Date());

  if (!isAfter(parsedExpiryDate, parsedIssueDate)) {
    return INVALID_DATE_MESSAGE;
  }
};

export const handleItalianPassportValidationExpirationDate = (expiryDate: string, issueDate: string) => {
  if (!expiryDate) {
    return MANDATORY_FIELD_MESSAGE;
  }

  const parsedExpiryDate = parse(expiryDate, DATE_FORMAT, new Date());
  const parsedIssueDate = parse(issueDate, DATE_FORMAT, new Date());
  const expectedExpiryDate = subDays(addYears(parsedIssueDate, 10), 1);

  if (!isEqual(parsedExpiryDate, expectedExpiryDate)) {
    return INVALID_DATE_MESSAGE;
  }
};

export const getCieExpireDate = (issueDate: Date, birthDate: Date) => {
  const yearsToAdd = isAfterOrEqual(issueDate, birthDate) ? 10 : 9;
  return addYears(setYear(birthDate, getYear(issueDate)), yearsToAdd);
};

export const getIdCardExpirationDateByBirthDate = (
  birthDate: string,
  issueDate: string,
  documentType: DocumentType
) => {
  const parsedBirthDate = parse(birthDate, DATE_FORMAT, new Date());
  const parsedIssueDate = parse(issueDate, DATE_FORMAT, new Date());
  const birthDateMonth = getMonth(parsedBirthDate) + 1;
  const birthDateDay = getDate(parsedBirthDate);
  const issueDateYear = getYear(parsedIssueDate);
  const issueDateMonth = getMonth(parsedIssueDate) + 1;
  const issueDateDay = getDate(parsedIssueDate);
  let expiringYear = isIssueDateAfterBirthDate(issueDateMonth, birthDateMonth, issueDateDay, birthDateDay)
    ? issueDateYear + 11
    : issueDateYear + 10;

  if (
    isAfter(parsedIssueDate, NEW_EXPIRATION_DATE_INITIAL_DATE) &&
    !isBirthDateFebruaryThe29th(birthDate) &&
    documentType === DocumentType.ID_CARD
  ) {
    return getCieExpireDate(parsedIssueDate, parsedBirthDate);
  } else {
    return new Date(`${birthDateMonth}/${birthDateDay}/${expiringYear}`);
  }
};

const isBirthDateFebruaryThe29th = (birthDate: string) => {
  return (
    getMonth(parse(birthDate, DATE_FORMAT, new Date())) === 1 &&
    getDate(parse(birthDate, DATE_FORMAT, new Date())) === 29
  );
};

const isAfterOrEqual = (issueDate: Date, birthDate: Date) => {
  const birthDateWithIssueDateYear = setYear(birthDate, getYear(issueDate));
  return isEqual(issueDate, birthDateWithIssueDateYear) || isAfter(issueDate, birthDateWithIssueDateYear);
};

const areIssueDateMonthAndDayEqualToBirthMonthAndDay = (parsedExpiryDate: Date, birthDate: string) => {
  return (
    getMonth(parsedExpiryDate) + 1 === getMonth(parse(birthDate, DATE_FORMAT, new Date())) + 1 ||
    getDate(parsedExpiryDate) === getDate(parse(birthDate, DATE_FORMAT, new Date()))
  );
};
function isIssueDateAfterBirthDate(
  issueDateMonth: number,
  birthDateMonth: number,
  issueDateDay: number,
  birthDateDay: number
) {
  return issueDateMonth > birthDateMonth || (issueDateMonth === birthDateMonth && issueDateDay > birthDateDay);
}
